// =============================================================================
// Confirm Step — riepilogo + accettazione termini + submit
// =============================================================================

import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { PrimaryButton } from './PrimaryButton';
import { useDateLocale } from '../../hooks/useDateLocale';
import type { PublicService, PublicTenantInfo } from '../../api/public-booking-hooks';
import type { CustomerDetails } from './DetailsStep';
import { formatInTz, timeZoneAbbreviation } from '../../lib/tenantTime';

interface Props {
  service: PublicService;
  startIso: string;
  customer: CustomerDetails;
  tenant: PublicTenantInfo;
  onBack: () => void;
  onSubmit: (acceptTerms: boolean) => void | Promise<void>;
  isSubmitting: boolean;
}

export function ConfirmStep({ service, startIso, customer, tenant, onBack, onSubmit, isSubmitting }: Props) {
  const { t } = useTranslation('public-booking');
  const dateLocale = useDateLocale();
  const [accept, setAccept] = useState(false);

  const start = new Date(startIso);
  const end = new Date(start.getTime() + service.durationMinutes * 60_000);

  const hasLinks = !!(tenant.termsUrl || tenant.privacyUrl);

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900">{t('confirm.title')}</h2>

      <dl className="space-y-3 bg-gray-50 rounded-lg p-4 text-sm">
        <Row label={t('confirm.service')} value={service.name} />
        <Row
          label={t('confirm.when')}
          // Riepilogo prima dell'invio: e' il punto in cui un orario sbagliato
          // costa di piu', quindi il fuso dell'attivita' e' esplicito.
          value={
            `${formatInTz(start, tenant.timezone, 'PPPp', dateLocale)}` +
            ` → ${formatInTz(end, tenant.timezone, 'p', dateLocale)}` +
            ` (${timeZoneAbbreviation(tenant.timezone, start)})`
          }
        />
        <Row label={t('confirm.duration')} value={`${service.durationMinutes} ${t('confirm.minutes')}`} />
        <Row label={t('confirm.name')} value={`${customer.firstName} ${customer.lastName}`} />
        <Row
          label={t('confirm.contact')}
          value={[customer.email, customer.phone].filter(Boolean).join(' · ')}
        />
      </dl>

      {service.mode === 'approval' && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          {t('confirm.approvalNotice')}
        </div>
      )}

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={accept}
          onChange={(e) => setAccept(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300"
        />
        <span className="text-sm text-gray-700">
          {hasLinks ? (
            <Trans
              t={t}
              i18nKey="confirm.termsLabel"
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                1: tenant.termsUrl ? (
                  <a href={tenant.termsUrl} target="_blank" rel="noreferrer" className="underline" />
                ) : (
                  <span />
                ),
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                2: tenant.privacyUrl ? (
                  <a href={tenant.privacyUrl} target="_blank" rel="noreferrer" className="underline" />
                ) : (
                  <span />
                ),
              }}
              values={{ terms: t('confirm.terms'), privacy: t('confirm.privacy') }}
            />
          ) : (
            t('confirm.termsShort')
          )}
        </span>
      </label>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <button onClick={onBack} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
          ← {t('actions.back')}
        </button>
        <PrimaryButton disabled={!accept || isSubmitting} onClick={() => onSubmit(accept)}>
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {t('confirm.submit')}
        </PrimaryButton>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs uppercase text-gray-500 flex-shrink-0">{label}</dt>
      <dd className="text-gray-900 text-right break-words">{value}</dd>
    </div>
  );
}
